import { useCallback, useEffect, useState } from "react";
import { userReportsApi } from "../api";
import { getApiErrorMessage } from "../utils";
import type { PublicReportPage } from "../types/reports";

const PAGE_SIZE = 12;

export function useDiscoverReports(active: boolean) {
  const [page, setPage] = useState<PublicReportPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const load = useCallback(async (pageNumber: number, query: string) => {
    setLoading(true);
    setError("");
    try {
      const response = await userReportsApi.discover({
        search: query.trim() || undefined,
        page: pageNumber,
        page_size: PAGE_SIZE,
      });
      setPage(response.data);
    } catch (caught) {
      setError(getApiErrorMessage(caught, "Could not load public reports"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    const timeout = window.setTimeout(() => void load(currentPage, search), search ? 300 : 0);
    return () => window.clearTimeout(timeout);
  }, [active, currentPage, load, search]);

  const setSearch = useCallback((value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  }, []);

  const reload = useCallback(() => load(currentPage, search), [load, currentPage, search]);

  return {
    page,
    loading,
    error,
    search,
    setSearch,
    currentPage,
    setCurrentPage,
    reload,
    pageSize: PAGE_SIZE,
  };
}
